import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Zap, Images, Home, Building, Store, Hotel, Warehouse, Waves, LayoutGrid, Upload, FileCode, FileText, Ruler } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { PageHeader } from "@/components/PageParts";
import PlanSVG from "@/components/PlanSVG";
import { Button, Card, Input, Badge } from "@/components/ui";

const KINDS = [
  { id: "villa", icon: Home, ar: "فيلا", en: "Villa" },
  { id: "apartment", icon: Building, ar: "شقة", en: "Apartment" },
  { id: "studio_apartments", icon: LayoutGrid, ar: "استوديوهات", en: "Studios" },
  { id: "apartments_floor", icon: Building, ar: "شقق كاملة", en: "Apartments" },
  { id: "office", icon: Building, ar: "مكاتب", en: "Office" },
  { id: "hotel", icon: Hotel, ar: "فندق", en: "Hotel" },
  { id: "shops", icon: Store, ar: "محلات", en: "Shops" },
  { id: "shop_house", icon: Store, ar: "محلات + سكن", en: "Shop-house" },
  { id: "basement", icon: Warehouse, ar: "بدروم / قبو", en: "Basement" },
  { id: "mall", icon: Warehouse, ar: "مول", en: "Mall" },
  { id: "pool", icon: Waves, ar: "مسبح", en: "Pool" },
];

const AMENITIES = [
  ["has_pool", "مسبح", "Pool"],
  ["has_garden", "حديقة", "Garden"],
  ["has_courtyard", "فناء", "Courtyard"],
  ["has_garage", "كراج", "Garage"],
  ["has_maid_room", "غرفة خادمة", "Maid room"],
  ["has_office", "مكتب", "Office"],
  ["has_master_ensuite", "جناح رئيسي", "Master ensuite"],
  ["has_balconies", "شرفات", "Balconies"],
  ["has_basement", "قبو تحت البناء", "Basement"],
  ["has_front_shops", "محلات على الواجهة", "Street-front shops"],
];

// Slab / floor systems - kept in parity with the mobile SlabSystemHelp list so
// the web user can choose ANY structural floor system (including Hordi and
// precast-rib Hordi) exactly like the mobile app.
const SLAB_SYSTEMS = [
  ["hordi_precast", "هوردي بأعصاب جاهزة", "Hordi (precast ribs)"],
  ["hordi_regular", "هوردي تقليدي", "Hordi (cast-in-place)"],
  ["solid_two_way", "مصمتة باتجاهين", "Two-way solid"],
  ["solid_one_way", "مصمتة باتجاه واحد", "One-way solid"],
  ["flat_slab", "بلاطة مسطحة (فلات سلاب)", "Flat slab"],
  ["waffle", "أعصاب متقاطعة (وافل)", "Waffle"],
  ["hollow_core_precast", "أعصاب مجوّفة سابقة الصب", "Hollow-core precast"],
  ["composite_deck", "بلاطة مركّبة (Deck)", "Composite deck"],
];

export default function Studio() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [wm, setWm] = useWorkingModel();

  const [kind, setKind] = useState("villa");
  const [plotW, setPlotW] = useState("18");
  const [plotH, setPlotH] = useState("22");
  const [floors, setFloors] = useState("1");
  const [bedrooms, setBedrooms] = useState("3");
  const [regularBedrooms, setRegularBedrooms] = useState("2");
  const [kidsRooms, setKidsRooms] = useState("1");
  const [numKitchens, setNumKitchens] = useState("1");
  const [bathrooms, setBathrooms] = useState("2");
  const [neighborSide, setNeighborSide] = useState("none");
  const [numApartments, setNumApartments] = useState("4");
  const [bedroomsPerUnit, setBedroomsPerUnit] = useState("0");
  const [basementUsage, setBasementUsage] = useState("parking");
  const [hasRamp, setHasRamp] = useState(true);
  const [numStores, setNumStores] = useState("6");
  const [numFrontShops, setNumFrontShops] = useState("2");
  const [hasShowerRoom, setHasShowerRoom] = useState(true);
  const [hasMachineRoom, setHasMachineRoom] = useState(true);
  const [structuralSystem, setStructuralSystem] = useState("rc_frame");
  const [slabSystem, setSlabSystem] = useState("hordi_precast");
  const [codeProfile, setCodeProfile] = useState("SBC");
  const [amen, setAmen] = useState([]);
  const [poolSide, setPoolSide] = useState("rear");
  const [courtyardSide, setCourtyardSide] = useState("center");
  const [gardenSide, setGardenSide] = useState("front");
  const [busy, setBusy] = useState(null);
  const [importing, setImporting] = useState(null);
  const [importReport, setImportReport] = useState(null);
  const [showDims, setShowDims] = useState(true);

  const toggle = (a) => setAmen((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));

  // Context-aware inputs: only show fields that make engineering sense for the
  // selected building type (e.g. a Pool has no bedrooms / floors / slab system).
  const isPool = kind === "pool";
  const isResidential = kind === "villa" || kind === "apartment";
  const showRooms = ["villa", "apartment", "apartments_floor", "studio_apartments", "shop_house"].includes(kind)
    || (kind === "basement" && basementUsage === "apartment");
  const showFloors = !isPool;
  const showStruct = !isPool;
  const showAmenities = ["villa", "apartment"].includes(kind);
  const showNeighbor = !isPool;

  const brief = () => {
    const b = {
      kind,
      plot_w: parseFloat(plotW) || 18,
      plot_h: parseFloat(plotH) || 22,
      floors: parseInt(floors, 10) || 1,
      bedrooms: parseInt(bedrooms, 10) || 3,
      bathrooms: parseInt(bathrooms, 10) || 2,
      neighbor_side: neighborSide,
      structural_system: structuralSystem,
      slab_system: slabSystem,
      code_profile: codeProfile,
      lang,
    };
    if (kind === "apartments_floor") {
      b.num_apartments = parseInt(numApartments, 10) || 4;
      b.bedrooms_per_unit = parseInt(bedroomsPerUnit, 10) || 0;
    }
    if (isResidential) {
      b.regular_bedrooms = parseInt(regularBedrooms, 10) || 0;
      b.kids_rooms = parseInt(kidsRooms, 10) || 0;
      b.num_kitchens = parseInt(numKitchens, 10) || 1;
      const total = (amen.includes("has_master_ensuite") ? 1 : 0) + b.regular_bedrooms + b.kids_rooms;
      if (total >= 1) b.bedrooms = total;
    }
    AMENITIES.forEach(([k]) => { if (amen.includes(k)) b[k] = true; });
    if (amen.includes("has_pool"))      b.pool_side = poolSide;
    if (amen.includes("has_courtyard")) b.courtyard_side = courtyardSide;
    if (amen.includes("has_garden"))    b.garden_side = gardenSide;
    if (kind === "villa" && amen.includes("has_basement")) b.basement_use = basementUsage;
    return b;
  };

  async function generateVariants() {
    const b = brief();
    // For apartments_floor the gallery should show diverse counts/bedroom
    // mixes, so we let the variant catalog drive those (only the language
    // and neighbour context carry over). "Generate one" honours the exact
    // count the user typed.
    if (kind === "apartments_floor") {
      delete b.num_apartments;
      delete b.bedrooms_per_unit;
    }
    const q = new URLSearchParams(
      Object.entries(b).reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
    );
    navigate(`/variants?${q.toString()}`);
  }

  async function generateOne() {
    setBusy("one");
    try {
      const b = brief();
      let res;
      if (kind === "villa") {
        res = await api.generateVilla({
          land_w: b.plot_w, land_h: b.plot_h, bedrooms: b.bedrooms, bathrooms: b.bathrooms,
          regular_bedrooms: b.regular_bedrooms, kids_rooms: b.kids_rooms, num_kitchens: b.num_kitchens,
          basement_use: b.basement_use,
          has_pool: !!b.has_pool, pool_side: b.pool_side || "rear",
          has_garden: !!b.has_garden, garden_side: b.garden_side || "front",
          has_courtyard: !!b.has_courtyard, courtyard_side: b.courtyard_side || "center",
          has_garage: !!b.has_garage, has_maid_room: !!b.has_maid_room, has_office: !!b.has_office,
          has_master_ensuite: !!b.has_master_ensuite, has_balconies: !!b.has_balconies,
          has_basement: !!b.has_basement, basement_depth_m: 3.0,
          has_front_shops: !!b.has_front_shops, num_front_shops: 0,
          neighbor_side: neighborSide,
          floors: b.floors, structural_system: b.structural_system, code_profile: b.code_profile,
          lang,
          project_name: isAr ? `فيلا ${plotW}×${plotH}` : `Villa ${plotW}×${plotH}`,
        });
      } else if (kind === "apartment") {
        res = await api.generateApartment({
          width: b.plot_w, height: b.plot_h, bedrooms: b.bedrooms, bathrooms: b.bathrooms,
          regular_bedrooms: b.regular_bedrooms, kids_rooms: b.kids_rooms, num_kitchens: b.num_kitchens,
          has_master_ensuite: !!b.has_master_ensuite,
          floors: b.floors, structural_system: b.structural_system, code_profile: b.code_profile,
          lang,
          project_name: isAr ? `شقة ${plotW}×${plotH}` : `Apartment ${plotW}×${plotH}`,
        });
      } else {
        const extra = {
          width: b.plot_w, height: b.plot_h, plot_w: b.plot_w, plot_h: b.plot_h,
          land_w: b.plot_w, land_h: b.plot_h, floors: b.floors,
          structural_system: b.structural_system, code_profile: b.code_profile,
          neighbor_side: neighborSide, lang,
        };
        if (kind === "apartments_floor") {
          extra.num_apartments = b.num_apartments;
          extra.bedrooms_per_unit = b.bedrooms_per_unit;
        }
        if (kind === "basement") {
          extra.usage = basementUsage;
          extra.has_ramp = !!hasRamp;
          extra.num_stores = parseInt(numStores, 10) || 6;
          extra.bedrooms = b.bedrooms;
        }
        if (kind === "shop_house") {
          extra.num_front_shops = parseInt(numFrontShops, 10) || 2;
          extra.bedrooms = b.bedrooms;
        }
        if (kind === "pool") {
          extra.has_shower_room = hasShowerRoom;
          extra.has_machine_room = hasMachineRoom;
        }
        res = await api.editorGenerate(kind, extra);
      }
      const title = isAr ? `${KINDS.find((k) => k.id === kind)?.ar} ${plotW}×${plotH}` : `${KINDS.find((k) => k.id === kind)?.en} ${plotW}×${plotH}`;
      setWm({ model: res.model, kind, floors: b.floors, slab_system: slabSystem, has_basement: !!b.has_basement, basement_depth_m: 3.0, design_code: codeProfile === "SBC" ? "SBC_304" : codeProfile === "ACI" ? "ACI_318" : "EC2", generation_params: { plot_w: b.plot_w, plot_h: b.plot_h }, title });
      toast.success(isAr ? "تم توليد المخطط" : "Plan generated");
      navigate("/editor");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(null);
    }
  }

  async function importFile(fmt /* "dxf" | "pdf" */) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = fmt === "pdf" ? ".pdf" : ".dxf";
    const file = await new Promise((resolve) => {
      input.onchange = () => resolve((input.files || [])[0] || null);
      input.click();
    });
    if (!file) return;
    setImporting(fmt);
    setImportReport(null);
    try {
      const res = await api.editorImportFile(file, fmt, true);
      const title = file.name.replace(/\.[^.]+$/, "");
      setWm({
        model: res.model, kind: "imported", floors: 1,
        slab_system: "hordi_regular", design_code: "SBC_304", title,
      });
      setImportReport({ ...(res.completion || {}), stats: res.stats, method: res.import_method });
      toast.success(isAr ? "تم الاستيراد والإكمال التلقائي" : "Imported & auto-completed");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setImporting(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow={isAr ? "الاستوديو" : "Studio"}
        title={isAr ? "أنشئ مخططاً معمارياً" : "Create an architectural plan"}
        subtitle={isAr ? "اختر نوع المبنى والأبعاد ثم ولّد 10 مخططات متنوعة أو مخططاً واحداً فوراً." : "Pick a building type and dimensions, then generate 10 diverse variants or a single plan instantly."}
      />
      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <Card>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">{isAr ? "نوع المبنى" : "Building type"}</p>
          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {KINDS.map((k) => (
              <button
                key={k.id}
                data-testid={`kind-${k.id}`}
                onClick={() => setKind(k.id)}
                className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 transition-colors ${kind === k.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/50 hover:border-primary/40"}`}
              >
                <k.icon size={20} />
                <span className="text-xs font-bold">{isAr ? k.ar : k.en}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Input label={isAr ? "الواجهة (م)" : "Frontage (m)"} value={plotW} onChange={(e) => setPlotW(e.target.value)} inputMode="decimal" data-testid="plot-w" />
            <Input label={isAr ? "العمق (م)" : "Depth (m)"} value={plotH} onChange={(e) => setPlotH(e.target.value)} inputMode="decimal" data-testid="plot-h" />
            {showFloors && <Input label={isAr ? "الطوابق" : "Floors"} value={floors} onChange={(e) => setFloors(e.target.value)} inputMode="numeric" data-testid="floors" />}
            {showRooms && !isResidential && <Input label={isAr ? "غرف النوم" : "Bedrooms"} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} inputMode="numeric" data-testid="bedrooms" />}
            {showRooms && !isResidential && <Input label={isAr ? "الحمامات" : "Bathrooms"} value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} inputMode="numeric" data-testid="bathrooms" />}
          </div>

          {isResidential && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {isAr ? "الفراغات التفصيلية" : "Detailed spaces"}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Input label={isAr ? "غرف نوم عادية" : "Regular bedrooms"} value={regularBedrooms} onChange={(e) => setRegularBedrooms(e.target.value)} inputMode="numeric" data-testid="regular-bedrooms" />
                <Input label={isAr ? "غرف أطفال" : "Kids rooms"} value={kidsRooms} onChange={(e) => setKidsRooms(e.target.value)} inputMode="numeric" data-testid="kids-rooms" />
                <Input label={isAr ? "الحمامات" : "Bathrooms"} value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} inputMode="numeric" data-testid="bathrooms" />
                <Input label={isAr ? "المطابخ" : "Kitchens"} value={numKitchens} onChange={(e) => setNumKitchens(e.target.value)} inputMode="numeric" data-testid="num-kitchens" />
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {isAr
                  ? `الإجمالي: ${(amen.includes("has_master_ensuite") ? 1 : 0) + (parseInt(regularBedrooms, 10) || 0) + (parseInt(kidsRooms, 10) || 0)} غرفة نوم (تشمل الماستر إن فُعّل). كل الغرف تُوصل عبر المداخل/الممرات فقط، والحمامات لا تُدخل من الغرف عدا حمام الماستر.`
                  : `Total: ${(amen.includes("has_master_ensuite") ? 1 : 0) + (parseInt(regularBedrooms, 10) || 0) + (parseInt(kidsRooms, 10) || 0)} bedrooms (incl. master if enabled). Every room is reached via entrances/corridors only; baths are never entered through a room except the master ensuite.`}
              </p>
            </div>
          )}

          {showStruct && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-bold text-muted-foreground">{isAr ? "النظام الإنشائي (يحدد مقاطع الأعمدة والبحور)" : "Structural system (drives column sizes + bay spans)"}</p>
            <div className="flex flex-wrap gap-2">
              {[["rc_frame", "هيكل خرساني (أعمدة)", "RC frame"], ["load_bearing", "جدران حاملة", "Load-bearing"]].map(([id, ar, en]) => (
                <button key={id} data-testid={`struct-sys-${id}`} onClick={() => setStructuralSystem(id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${structuralSystem === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                  {isAr ? ar : en}
                </button>
              ))}
            </div>
            <p className="mb-2 mt-3 text-xs font-bold text-muted-foreground">{isAr ? "كود التصميم" : "Design code"}</p>
            <div className="flex flex-wrap gap-2">
              {[["SBC", "SBC (سعودي)", "SBC"], ["ACI", "ACI (أمريكي)", "ACI"], ["EC2", "Eurocode 2", "EC2"]].map(([id, ar, en]) => (
                <button key={id} data-testid={`code-${id}`} onClick={() => setCodeProfile(id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${codeProfile === id ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                  {isAr ? ar : en}
                </button>
              ))}
            </div>
            <p className="mb-2 mt-3 text-xs font-bold text-muted-foreground">{isAr ? "نظام السقف/البلاطة (هوردي، مصمتة، مسطحة…)" : "Floor / slab system (Hordi, solid, flat…)"}</p>
            <div className="flex flex-wrap gap-2">
              {SLAB_SYSTEMS.map(([id, ar, en]) => (
                <button key={id} data-testid={`slab-sys-${id}`} onClick={() => setSlabSystem(id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${slabSystem === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                  {isAr ? ar : en}
                </button>
              ))}
            </div>
          </div>
          )}

          {isPool && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-bold text-muted-foreground">{isAr ? "خيارات المسبح" : "Pool options"}</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="pool-shower" onClick={() => setHasShowerRoom((v) => !v)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${hasShowerRoom ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                  {isAr ? (hasShowerRoom ? "غرفة دش/تبديل ✓" : "بدون دش") : (hasShowerRoom ? "Shower/changing ✓" : "No shower")}
                </button>
                <button data-testid="pool-machine" onClick={() => setHasMachineRoom((v) => !v)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${hasMachineRoom ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                  {isAr ? (hasMachineRoom ? "غرفة مضخات وفلترة ✓" : "بدون غرفة مضخات") : (hasMachineRoom ? "Pump/filter room ✓" : "No pump room")}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {isAr ? "مسبح رئيسي 8×4×1.5م + مسبح أطفال + ممشى محيط، مع غرفة دش وغرفة مضخات/فلترة اختيارية. لا يحتوي غرفاً سكنية أو سقفاً إنشائياً." : "Main pool 8×4×1.5 m + kids pool + surrounding deck, with an optional shower + pump/filtration room. No bedrooms or structural roof."}
              </p>
            </div>
          )}

          {kind === "apartments_floor" && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Input
                label={isAr ? "عدد الشقق بالطابق" : "Apartments per floor"}
                value={numApartments}
                onChange={(e) => setNumApartments(e.target.value)}
                inputMode="numeric"
                data-testid="num-apartments"
              />
              <Input
                label={isAr ? "غرف نوم لكل شقة (0 = تلقائي)" : "Bedrooms/unit (0 = auto)"}
                value={bedroomsPerUnit}
                onChange={(e) => setBedroomsPerUnit(e.target.value)}
                inputMode="numeric"
                data-testid="bedrooms-per-unit"
              />
              <p className="col-span-2 text-[11px] text-muted-foreground">
                {isAr
                  ? "اختر بحرية عدد الشقق؛ إن لم يتّسع العدد للأرض يُضبط تلقائياً مع تنبيه. كل شقة: صالة + غرف نوم + مطبخ + حمام."
                  : "Pick any apartment count; if it won't fit the plot it's auto-capped with a notice. Each unit: living + bedrooms + kitchen + bath."}
              </p>
            </div>
          )}

          {kind === "basement" && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-bold text-muted-foreground">{isAr ? "استخدام البدروم" : "Basement usage"}</p>
              <div className="flex flex-wrap gap-2">
                {[["parking", "مواقف سيارات", "Parking"], ["storage", "تخزين", "Storage"], ["apartment", "شقة سكنية", "Apartment"]].map(([id, ar, en]) => (
                  <button key={id} data-testid={`basement-usage-${id}`} onClick={() => setBasementUsage(id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${basementUsage === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                    {isAr ? ar : en}
                  </button>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {basementUsage === "parking" && (
                  <button data-testid="basement-ramp" onClick={() => setHasRamp((v) => !v)}
                    className={`rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${hasRamp ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card/50"}`}>
                    {isAr ? (hasRamp ? "مع منحدر سيارات ✓" : "بدون منحدر") : (hasRamp ? "With car ramp ✓" : "No ramp")}
                  </button>
                )}
                {basementUsage === "storage" && (
                  <Input label={isAr ? "عدد المخازن" : "Store rooms"} value={numStores}
                    onChange={(e) => setNumStores(e.target.value)} inputMode="numeric" data-testid="basement-num-stores" />
                )}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {isAr ? "جدران خرسانية استنادية + عزل مائي + تهوية ميكانيكية ومناور. يُولَّد كطابق مستقل بكامل مخططاته للأرض نفسها." : "RC retaining walls + tanking + mechanical ventilation & light-wells. Generated as a standalone floor with its full plan set for the same plot."}
              </p>
            </div>
          )}

          {kind === "shop_house" && (
            <div className="mt-4">
              <Input label={isAr ? "عدد المحلات في الواجهة" : "Front shops"} value={numFrontShops}
                onChange={(e) => setNumFrontShops(e.target.value)} inputMode="numeric" data-testid="num-front-shops" />
              <p className="mt-2 text-[11px] text-muted-foreground">
                {isAr ? "محلات على الواجهة ومدخل العمارة بينها + شقة خلفية. يزيد عدد المحلات كلما اتّسعت الواجهة (٣.٥م لكل محل)." : "Street shops with the building entrance between them + apartment behind. More shops fit as the facade widens (3.5 m each)."}
              </p>
            </div>
          )}
          {showAmenities && (
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map(([k, ar, en]) => (
              <button
                key={k}
                data-testid={`amen-${k}`}
                onClick={() => toggle(k)}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${amen.includes(k) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}
              >
                {isAr ? ar : en}
              </button>
            ))}
          </div>
          )}

          {showAmenities && amen.includes("has_pool") && (
            <div className="mt-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {isAr ? "جهة المسبح" : "Pool side"}
              </p>
              <div className="flex flex-wrap gap-2">
                {[["front","أمام","Front"],["rear","خلف","Rear"],["left","يسار","Left"],["right","يمين","Right"]].map(([id, ar, en]) => (
                  <button key={id} data-testid={`pool-side-${id}`} onClick={() => setPoolSide(id)}
                    className={`rounded-full border px-3 py-1 text-[11px] font-bold ${poolSide === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                    {isAr ? ar : en}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showAmenities && amen.includes("has_courtyard") && (
            <div className="mt-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {isAr ? "جهة الفناء الداخلي" : "Courtyard side"}
              </p>
              <div className="flex flex-wrap gap-2">
                {[["center","وسط","Center"],["front","أمام","Front"],["rear","خلف","Rear"],["left","يسار","Left"],["right","يمين","Right"]].map(([id, ar, en]) => (
                  <button key={id} data-testid={`courtyard-side-${id}`} onClick={() => setCourtyardSide(id)}
                    className={`rounded-full border px-3 py-1 text-[11px] font-bold ${courtyardSide === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                    {isAr ? ar : en}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showAmenities && amen.includes("has_garden") && (
            <div className="mt-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {isAr ? "جهة الحديقة" : "Garden side"}
              </p>
              <div className="flex flex-wrap gap-2">
                {[["front","أمام","Front"],["rear","خلف","Rear"]].map(([id, ar, en]) => (
                  <button key={id} data-testid={`garden-side-${id}`} onClick={() => setGardenSide(id)}
                    className={`rounded-full border px-3 py-1 text-[11px] font-bold ${gardenSide === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}>
                    {isAr ? ar : en}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showAmenities && (amen.includes("has_pool") || amen.includes("has_garden") || amen.includes("has_courtyard")) && (
            <div className="mt-4 rounded-lg border border-border/60 bg-card/30 p-3" data-testid="amenity-preview">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {isAr ? "معاينة مواضع الأصول" : "Amenity placement preview"}
              </p>
              {(() => {
                // Small thumbnail sketch of the plot with ghost-outlined
                // pool / courtyard / garden on the chosen side so the user
                // sees placement BEFORE hitting Generate.
                const lw = Math.max(6, Math.min(60, Number(plotW) || 20));
                const lh = Math.max(6, Math.min(60, Number(plotH) || 20));
                const W = 240, H = 160, pad = 12;
                const sc = Math.min((W - 2 * pad) / lw, (H - 2 * pad) / lh);
                const ox = (W - lw * sc) / 2;
                const oy = (H - lh * sc) / 2;
                // FRONT is at BOTTOM (large screen y), matching PlanSVG convention.
                const PX = (x) => ox + x * sc;
                const PY = (y) => oy + (lh - y) * sc;
                const setback = 2.0;
                const inset = { x0: PX(setback), y0: PY(lh - setback), x1: PX(lw - setback), y1: PY(setback) };
                const ghost = (side, wm, hm, label, fill) => {
                  const half_w = wm / 2, half_h = hm / 2;
                  let cx, cy;
                  if (side === "front") { cx = lw / 2; cy = setback + half_h; }
                  else if (side === "rear") { cx = lw / 2; cy = lh - setback - half_h; }
                  else if (side === "left") { cx = setback + half_w; cy = lh / 2; }
                  else if (side === "right") { cx = lw - setback - half_w; cy = lh / 2; }
                  else { cx = lw / 2; cy = lh / 2; } // center
                  return (
                    <g key={label}>
                      <rect x={PX(cx - half_w)} y={PY(cy + half_h)}
                            width={wm * sc} height={hm * sc}
                            rx={4} fill={fill} fillOpacity={0.35} stroke={fill} strokeWidth={1.2} strokeDasharray="3 2" />
                      <text x={PX(cx)} y={PY(cy) + 4} fontSize={9} fontWeight="800" textAnchor="middle" fill={fill}
                            style={{ paintOrder: "stroke", stroke: "#0F172A", strokeWidth: 2 }}>{label}</text>
                    </g>
                  );
                };
                return (
                  <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} data-testid="amenity-preview-svg" className="rounded bg-slate-950/40">
                    {/* plot outline */}
                    <rect x={PX(0)} y={PY(lh)} width={lw * sc} height={lh * sc}
                          fill="none" stroke="#DC2626" strokeWidth={1.2} strokeDasharray="6 3" />
                    {/* buildable envelope after setbacks */}
                    <rect x={inset.x0} y={inset.y0} width={inset.x1 - inset.x0} height={inset.y1 - inset.y0}
                          fill="#0EA5E9" fillOpacity={0.08} stroke="#0EA5E9" strokeWidth={0.6} />
                    {/* facade hint at bottom */}
                    <line x1={PX(0)} y1={PY(0) + 1.5} x2={PX(lw)} y2={PY(0) + 1.5} stroke="#16A34A" strokeWidth={2.5} />
                    <text x={PX(lw / 2)} y={PY(0) + 12} fontSize={8} fontWeight="800" textAnchor="middle" fill="#16A34A">
                      {isAr ? "🛣️ الشارع (الواجهة)" : "🛣️ Street (facade)"}
                    </text>
                    {amen.includes("has_pool")      && ghost(poolSide,      6, 3, isAr ? "🏊 مسبح" : "🏊 Pool",      "#0284C7")}
                    {amen.includes("has_courtyard") && ghost(courtyardSide, 5, 4, isAr ? "⛲ فناء" : "⛲ Courtyard", "#D97706")}
                    {amen.includes("has_garden")    && ghost(gardenSide,    lw - 4, 2, isAr ? "🌳 حديقة" : "🌳 Garden", "#16A34A")}
                  </svg>
                );
              })()}
              <p className="mt-2 text-[10px] text-muted-foreground">
                {isAr ? "الحدود المتقطعة = حد الأرض · الإطار السماوي = المنطقة القابلة للبناء" : "Dashed border = property line · Cyan box = buildable envelope"}
              </p>
            </div>
          )}

          {showNeighbor && (<>
          <p className="mb-2 mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {isAr ? "وضع الجيران (يضيف مناور للتهوية والإنارة على الجهة المحجوبة)" : "Neighbour situation (adds light-wells for ventilation/daylight on blocked sides)"}
          </p>
          <div className="flex flex-wrap gap-2">
            {[["none", "لا يوجد", "None"], ["left", "يسار", "Left"], ["right", "يمين", "Right"], ["both", "الجهتان", "Both sides"]].map(([id, ar, en]) => (
              <button
                key={id}
                data-testid={`neighbor-${id}`}
                onClick={() => setNeighborSide(id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${neighborSide === id ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card/50 hover:border-primary/40"}`}
              >
                {isAr ? ar : en}
              </button>
            ))}
          </div>
          </>)}

          <div className="mt-6 flex flex-wrap gap-3">
            <Button size="lg" onClick={generateVariants} data-testid="generate-variants-btn">
              <Images size={18} /> {isAr ? "ولّد 10 مخططات" : "Generate 10 variants"}
            </Button>
            <Button size="lg" variant="accent" onClick={generateOne} loading={busy === "one"} data-testid="generate-one-btn">
              <Zap size={18} /> {isAr ? "ولّد مخططاً واحداً فوراً" : "Generate one plan now"}
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400">
            <Sparkles size={13} /> {isAr ? `${parseFloat(plotW) * parseFloat(plotH) || 0} م² · QA آلي بدون أخطاء` : `${parseFloat(plotW) * parseFloat(plotH) || 0} m² · automatic zero-error QA`}
          </div>
        </Card>

        <Card className="flex flex-col">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-bold">{isAr ? "المخطط الحالي" : "Current plan"}</p>
            <div className="flex items-center gap-2">
              {wm?.model && (
                <button
                  type="button"
                  onClick={() => setShowDims((v) => !v)}
                  data-testid="studio-toggle-dims"
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${showDims ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                >
                  <Ruler size={12} /> {isAr ? "القياسات" : "Dimensions"}
                </button>
              )}
              {wm?.title && <Badge tone="primary">{wm.title}</Badge>}
            </div>
          </div>
          <div className="flex-1 overflow-hidden rounded-lg border border-border bg-white">
            {wm?.model ? <PlanSVG model={wm.model} w={520} h={420} showDims={showDims} showLabels={!showDims} explain lang={lang} /> : (
              <div className="flex h-full min-h-[300px] items-center justify-center p-6 text-center text-sm text-muted-foreground">
                {isAr ? "لم يتم توليد مخطط بعد. ابدأ بالتوليد على اليسار." : "No plan generated yet. Start on the left."}
              </div>
            )}
          </div>
          {wm?.model && (
            <p className="mt-2 text-xs font-semibold text-primary" data-testid="explain-hint">
              {isAr ? "اضغط أي عنصر في المخطط لعرض رسمه وشرحه الكامل" : "Tap any element in the plan to see its drawing + full explanation"}
            </p>
          )}
          {wm?.model && (
            <Button variant="outline" className="mt-3" onClick={() => navigate("/editor")}>
              {isAr ? "افتح في المحرر" : "Open in editor"}
            </Button>
          )}
        </Card>
      </div>

      {/* ── Import existing CAD (DXF / PDF) + deterministic auto-completion ── */}
      <Card className="mt-5">
        <div className="flex items-center gap-2">
          <Upload size={18} className="text-primary" />
          <p className="text-sm font-bold">{isAr ? "استيراد مخطط موجود وإكماله تلقائياً" : "Import an existing plan & auto-complete it"}</p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {isAr
            ? "ارفع ملف DXF من أوتوكاد أو PDF معماري. نحلّل الهندسة ونكمل الأجزاء الناقصة (سدّ الفجوات، تعديل الجدران للاستقامة، إغلاق المحيط، إضافة باب المدخل) بمنطق رياضي حتمي - بدون أي تكلفة."
            : "Upload an AutoCAD DXF or an architectural PDF. We analyse the geometry and complete the missing parts (heal gaps, straighten walls, close the perimeter, add an entrance door) using deterministic math - at no cost."}
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" loading={importing === "dxf"} onClick={() => importFile("dxf")} data-testid="import-dxf-btn">
            <FileCode size={16} /> {isAr ? "استيراد DXF" : "Import DXF"}
          </Button>
          <Button variant="outline" loading={importing === "pdf"} onClick={() => importFile("pdf")} data-testid="import-pdf-btn">
            <FileText size={16} /> {isAr ? "استيراد PDF" : "Import PDF"}
          </Button>
          <span className="self-center text-[11px] text-muted-foreground">
            {isAr ? "PDF متجهي = قياسات دقيقة · PDF ممسوح يُحلَّل بالرؤية الحاسوبية" : "Vector PDF = precise · scanned PDF via computer-vision"}
          </span>
        </div>

        {importReport && (
          <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="import-report">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-emerald-400">
              <Sparkles size={13} />
              {isAr ? "تقرير الإكمال التلقائي" : "Auto-completion report"}
              {importReport.method && (
                <Badge tone="primary" className="ml-1">
                  {importReport.method === "raster"
                    ? (isAr ? "PDF ممسوح (CV)" : "scanned (CV)")
                    : (isAr ? "متجهي" : "vector")}
                </Badge>
              )}
            </p>
            <div className="grid grid-cols-2 gap-1.5 text-[11px] text-muted-foreground sm:grid-cols-3">
              <span>{isAr ? "إجمالي الجدران" : "Total walls"}: <b className="text-foreground">{importReport.total_walls ?? importReport.stats?.walls ?? 0}</b></span>
              <span>{isAr ? "جدران استُقيمت" : "Straightened"}: <b className="text-foreground">{importReport.axis_aligned ?? 0}</b></span>
              <span>{isAr ? "فجوات سُدّت" : "Gaps closed"}: <b className="text-foreground">{importReport.gaps_closed ?? 0}</b></span>
              <span>{isAr ? "نقاط طُبّقت" : "Endpoints snapped"}: <b className="text-foreground">{importReport.endpoints_snapped ?? 0}</b></span>
              <span>{isAr ? "جدران محيط أُضيفت" : "Perimeter added"}: <b className="text-foreground">{importReport.perimeter_walls_added ?? 0}</b></span>
              <span>{isAr ? "أبواب أُضيفت" : "Doors added"}: <b className="text-foreground">{importReport.doors_added ?? 0}</b></span>
              <span>{isAr ? "مكررات حُذفت" : "Duplicates removed"}: <b className="text-foreground">{importReport.removed_duplicates ?? 0}</b></span>
              <span>{isAr ? "جدران خارجية" : "Exterior walls"}: <b className="text-foreground">{importReport.exterior_walls ?? 0}</b></span>
            </div>
            <Button variant="accent" className="mt-3" onClick={() => navigate("/editor")} data-testid="import-open-editor">
              {isAr ? "افتح المستورد في المحرر" : "Open imported plan in editor"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
